import { 
  doc, 
  getDoc, 
  updateDoc, 
  setDoc,
  onSnapshot,
  collection,
  addDoc,
  deleteDoc,
  query,
  orderBy,
  getDocs,
  Timestamp 
} from 'firebase/firestore';
import { db } from './firebase';

// Banner Interface (moved from bannerService)
export interface AdminBanner {
  id: string;
  text: string;
  backgroundColor: 'jamaica-green' | 'jamaica-black';
  isActive: boolean;
  order: number; // 1 or 2 for ordering
  link?: string; // Optional link to navigate when banner is clicked
  showCloseButton?: boolean; // Whether to show the close button
  countdown?: {
    enabled: boolean;
    endDate: string | Timestamp; // ISO date string or Firebase Timestamp
    format?: 'days' | 'hours' | 'minutes' | 'full'; // Display format
    prefix?: string; // Text before countdown (e.g., "Ends in:")
    suffix?: string; // Text after countdown (e.g., "remaining")
  };
  createdAt?: Date;
  updatedAt?: Date;
}

// FAQ Interface
export interface AdminFAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
  isPublished: boolean;
  order: number;
  isHelpful?: number; // Helpful votes count
  isNotHelpful?: number; // Not helpful votes count
  views?: number; // View count
  createdAt?: Date;
  updatedAt?: Date;
}

// Testimonial Interface
export interface AdminTestimonial {
  id: string;
  customerName: string;
  customerLocation?: string;
  rating: number;
  content: string;
  productId?: string;
  status: 'published' | 'unpublished';
  isVerified: boolean;
  avatar?: string;
  bgColor?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

// Configuration interfaces
export interface BannerConfig {
  banner1?: AdminBanner;
  banner2?: AdminBanner;
}

export interface FAQConfig {
  faqs: AdminFAQ[];
  lastUpdated?: Date;
}

export interface TestimonialConfig {
  testimonials: AdminTestimonial[];
  lastUpdated?: Date;
}

export interface ContentConfig {
  banners?: BannerConfig;
  faqs?: FAQConfig;
  testimonials?: TestimonialConfig;
}

/**
 * Get banner configuration from Firestore
 */
export const getBannerConfig = async (): Promise<BannerConfig> => {
  try {
    const bannerRef = doc(db, 'config', 'banners');
    const bannerDoc = await getDoc(bannerRef);
    
    if (!bannerDoc.exists()) {
      return {};
    }
    
    const data = bannerDoc.data();
    const config: BannerConfig = {};
    
    // Process banner1
    if (data.banner1) {
      config.banner1 = {
        id: 'banner1',
        text: data.banner1.text || '',
        backgroundColor: data.banner1.backgroundColor || 'jamaica-green',
        isActive: data.banner1.isActive || false,
        order: 1,
        link: data.banner1.link || undefined,
        showCloseButton: data.banner1.showCloseButton ?? false,
        countdown: data.banner1.countdown,
        createdAt: data.banner1.createdAt?.toDate ? data.banner1.createdAt.toDate() : new Date(),
        updatedAt: data.banner1.updatedAt?.toDate ? data.banner1.updatedAt.toDate() : new Date()
      };
    }
    
    // Process banner2
    if (data.banner2) {
      config.banner2 = {
        id: 'banner2',
        text: data.banner2.text || '',
        backgroundColor: data.banner2.backgroundColor || 'jamaica-black',
        isActive: data.banner2.isActive || false,
        order: 2,
        link: data.banner2.link || undefined,
        showCloseButton: data.banner2.showCloseButton ?? false,
        countdown: data.banner2.countdown,
        createdAt: data.banner2.createdAt?.toDate ? data.banner2.createdAt.toDate() : new Date(),
        updatedAt: data.banner2.updatedAt?.toDate ? data.banner2.updatedAt.toDate() : new Date()
      };
    }
    
    return config;
  } catch (error) {
    console.error('Error fetching banner config:', error);
    return {};
  }
};

/**
 * Update banner configuration in Firestore
 */
export const updateBannerConfig = async (config: BannerConfig): Promise<void> => {
  try {
    const bannerRef = doc(db, 'config', 'banners');
    const updateData: any = {};
    
    if (config.banner1) {
      updateData.banner1 = {
        text: config.banner1.text,
        backgroundColor: config.banner1.backgroundColor,
        isActive: config.banner1.isActive,
        link: config.banner1.link ?? null,
        showCloseButton: config.banner1.showCloseButton,
        updatedAt: new Date()
      };
      
      // Only include countdown if it's defined and not undefined
      if (config.banner1.countdown !== undefined) {
        updateData.banner1.countdown = config.banner1.countdown;
      }
    }
    
    if (config.banner2) {
      updateData.banner2 = {
        text: config.banner2.text,
        backgroundColor: config.banner2.backgroundColor,
        isActive: config.banner2.isActive,
        link: config.banner2.link ?? null,
        showCloseButton: config.banner2.showCloseButton,
        updatedAt: new Date()
      };
      
      // Only include countdown if it's defined and not undefined
      if (config.banner2.countdown !== undefined) {
        updateData.banner2.countdown = config.banner2.countdown;
      }
    }
    
    await setDoc(bannerRef, updateData, { merge: true });
  } catch (error) {
    console.error('Error updating banner config:', error);
    throw error;
  }
};

/**
 * Subscribe to banner configuration changes
 */
export const subscribeToBannerConfig = (
  callback: (config: BannerConfig) => void
): (() => void) => {
  const bannerRef = doc(db, 'config', 'banners');
  
  return onSnapshot(bannerRef, (doc) => {
    if (doc.exists()) {
      const data = doc.data();
      const config: BannerConfig = {};
      
      // Process banner1
      if (data.banner1) {
        config.banner1 = {
          id: 'banner1',
          text: data.banner1.text || '',
          backgroundColor: data.banner1.backgroundColor || 'jamaica-green',
          isActive: data.banner1.isActive || false,
          order: 1,
          link: data.banner1.link || undefined,
          showCloseButton: data.banner1.showCloseButton ?? false,
          countdown: data.banner1.countdown,
          createdAt: data.banner1.createdAt?.toDate ? data.banner1.createdAt.toDate() : new Date(),
          updatedAt: data.banner1.updatedAt?.toDate ? data.banner1.updatedAt.toDate() : new Date()
        };
      }
      
      // Process banner2
      if (data.banner2) {
        config.banner2 = {
          id: 'banner2',
          text: data.banner2.text || '',
          backgroundColor: data.banner2.backgroundColor || 'jamaica-black',
          isActive: data.banner2.isActive || false,
          order: 2,
          link: data.banner2.link || undefined,
          showCloseButton: data.banner2.showCloseButton ?? false,
          countdown: data.banner2.countdown,
          createdAt: data.banner2.createdAt?.toDate ? data.banner2.createdAt.toDate() : new Date(),
          updatedAt: data.banner2.updatedAt?.toDate ? data.banner2.updatedAt.toDate() : new Date()
        };
      }
      
      callback(config);
    } else {
      callback({});
    }
  }, (error) => {
    console.error('Error subscribing to banner config:', error);
    callback({});
  });
};

/**
 * Get FAQ configuration from Firestore
 */
export const getFAQConfig = async (): Promise<FAQConfig> => {
  try {
    const faqRef = doc(db, 'config', 'faqs');
    const faqDoc = await getDoc(faqRef);
    
    if (!faqDoc.exists()) {
      return { faqs: [] };
    }
    
    const data = faqDoc.data();
    const faqs: AdminFAQ[] = [];
    
    if (data.faqs && Array.isArray(data.faqs)) {
      data.faqs.forEach((faq: any, index: number) => {
        faqs.push({
          id: faq.id || `faq-${index}`,
          question: faq.question || '',
          answer: faq.answer || '',
          category: faq.category || 'General',
          isPublished: faq.isPublished ?? true,
          order: faq.order ?? index,
          isHelpful: faq.isHelpful || 0,
          isNotHelpful: faq.isNotHelpful || 0,
          views: faq.views || 0,
          createdAt: faq.createdAt?.toDate ? faq.createdAt.toDate() : new Date(),
          updatedAt: faq.updatedAt?.toDate ? faq.updatedAt.toDate() : new Date()
        });
      });
    }
    
    return {
      faqs: faqs.sort((a, b) => a.order - b.order),
      lastUpdated: data.lastUpdated?.toDate ? data.lastUpdated.toDate() : new Date()
    };
  } catch (error) {
    console.error('Error fetching FAQ config:', error);
    return { faqs: [] };
  }
};

/**
 * Get testimonial configuration from Firestore
 */
export const getTestimonialConfig = async (): Promise<TestimonialConfig> => {
  try {
    const testimonialRef = doc(db, 'config', 'testimonials');
    const testimonialDoc = await getDoc(testimonialRef);
    
    if (!testimonialDoc.exists()) {
      return { testimonials: [] };
    }
    
    const data = testimonialDoc.data();
    const testimonials: AdminTestimonial[] = [];
    
    if (data.testimonials && Array.isArray(data.testimonials)) {
      data.testimonials.forEach((testimonial: any, index: number) => {
        testimonials.push({
          id: testimonial.id || `testimonial-${index}`,
          customerName: testimonial.customerName || '',
          customerLocation: testimonial.customerLocation || '',
          rating: testimonial.rating || 5,
          content: testimonial.content || '',
          productId: testimonial.productId,
          status: testimonial.status || 'published',
          isVerified: testimonial.isVerified ?? false,
          avatar: testimonial.avatar,
          bgColor: testimonial.bgColor,
          createdAt: testimonial.createdAt?.toDate ? testimonial.createdAt.toDate() : new Date(),
          updatedAt: testimonial.updatedAt?.toDate ? testimonial.updatedAt.toDate() : new Date()
        });
      });
    }
    
    return {
      testimonials: testimonials.sort((a, b) => new Date(b.createdAt!).getTime() - new Date(a.createdAt!).getTime()),
      lastUpdated: data.lastUpdated?.toDate ? data.lastUpdated.toDate() : new Date()
    };
  } catch (error) {
    console.error('Error fetching testimonial config:', error);
    return { testimonials: [] };
  }
};

/**
 * Update FAQ configuration in Firestore
 */
export const updateFAQConfig = async (config: FAQConfig): Promise<void> => {
  try {
    const faqRef = doc(db, 'config', 'faqs');
    const updateData = {
      faqs: config.faqs.map(faq => ({
        id: faq.id,
        question: faq.question,
        answer: faq.answer,
        category: faq.category,
        isPublished: faq.isPublished,
        order: faq.order,
        isHelpful: faq.isHelpful || 0,
        isNotHelpful: faq.isNotHelpful || 0,
        views: faq.views || 0,
        createdAt: faq.createdAt || new Date(),
        updatedAt: new Date()
      })),
      lastUpdated: new Date()
    };
    
    await setDoc(faqRef, updateData, { merge: true });
  } catch (error) {
    console.error('Error updating FAQ config:', error);
    throw error;
  }
};

/**
 * Update testimonial configuration in Firestore
 */
export const updateTestimonialConfig = async (config: TestimonialConfig): Promise<void> => {
  try {
    const testimonialRef = doc(db, 'config', 'testimonials');
    const updateData = {
      testimonials: config.testimonials.map(testimonial => {
        // Include all fields, even optional ones (as empty strings if not provided)
        const testimonialData: any = {
          id: testimonial.id,
          customerName: testimonial.customerName,
          customerLocation: testimonial.customerLocation || '', // Always include, empty string if not provided
          rating: testimonial.rating,
          content: testimonial.content,
          productId: testimonial.productId || '', // Always include, empty string if not provided
          status: testimonial.status,
          isVerified: testimonial.isVerified,
          avatar: testimonial.avatar || '', // Always include, empty string if not provided
          bgColor: testimonial.bgColor,
          createdAt: testimonial.createdAt || new Date(),
          updatedAt: new Date()
        };

        return testimonialData;
      }),
      lastUpdated: new Date()
    };
    
    await setDoc(testimonialRef, updateData, { merge: true });
  } catch (error) {
    console.error('Error updating testimonial config:', error);
    throw error;
  }
};

/**
 * Subscribe to FAQ configuration changes
 */
export const subscribeToFAQConfig = (
  callback: (config: FAQConfig) => void
): (() => void) => {
  const faqRef = doc(db, 'config', 'faqs');
  
  return onSnapshot(faqRef, (doc) => {
    if (doc.exists()) {
      const data = doc.data();
      const faqs: AdminFAQ[] = [];
      
      if (data.faqs && Array.isArray(data.faqs)) {
        data.faqs.forEach((faq: any, index: number) => {
          faqs.push({
            id: faq.id || `faq-${index}`,
            question: faq.question || '',
            answer: faq.answer || '',
            category: faq.category || 'General',
            isPublished: faq.isPublished ?? true,
            order: faq.order ?? index,
            isHelpful: faq.isHelpful || 0,
            isNotHelpful: faq.isNotHelpful || 0,
            views: faq.views || 0,
            createdAt: faq.createdAt?.toDate ? faq.createdAt.toDate() : new Date(),
            updatedAt: faq.updatedAt?.toDate ? faq.updatedAt.toDate() : new Date()
          });
        });
      }
      
      callback({
        faqs: faqs.sort((a, b) => a.order - b.order),
        lastUpdated: data.lastUpdated?.toDate ? data.lastUpdated.toDate() : new Date()
      });
    } else {
      callback({ faqs: [] });
    }
  }, (error) => {
    console.error('Error subscribing to FAQ config:', error);
    callback({ faqs: [] });
  });
};

/**
 * Subscribe to testimonial configuration changes
 */
export const subscribeToTestimonialConfig = (
  callback: (config: TestimonialConfig) => void
): (() => void) => {
  const testimonialRef = doc(db, 'config', 'testimonials');
  
  return onSnapshot(testimonialRef, (doc) => {
    if (doc.exists()) {
      const data = doc.data();
      const testimonials: AdminTestimonial[] = [];
      
      if (data.testimonials && Array.isArray(data.testimonials)) {
        data.testimonials.forEach((testimonial: any, index: number) => {
          testimonials.push({
            id: testimonial.id || `testimonial-${index}`,
            customerName: testimonial.customerName || '',
            customerLocation: testimonial.customerLocation || '',
            rating: testimonial.rating || 5,
            content: testimonial.content || '',
            productId: testimonial.productId,
            status: testimonial.status || 'published',
            isVerified: testimonial.isVerified ?? false,
            avatar: testimonial.avatar,
            bgColor: testimonial.bgColor,
            createdAt: testimonial.createdAt?.toDate ? testimonial.createdAt.toDate() : new Date(),
            updatedAt: testimonial.updatedAt?.toDate ? testimonial.updatedAt.toDate() : new Date()
          });
        });
      }
      
      callback({
        testimonials: testimonials.sort((a, b) => new Date(b.createdAt!).getTime() - new Date(a.createdAt!).getTime()),
        lastUpdated: data.lastUpdated?.toDate ? data.lastUpdated.toDate() : new Date()
      });
    } else {
      callback({ testimonials: [] });
    }
  }, (error) => {
    console.error('Error subscribing to testimonial config:', error);
    callback({ testimonials: [] });
  });
}; 
